import { braintreeDeviceDataCorrelationOnly } from './braintree-device-data';

/**
 * Query-string keys that the PayPal app-switch return URL can legitimately
 * carry. The trampoline mirrors these into sessionStorage (because Angular's
 * router has been observed to strip non-standard keys mid-navigation), and
 * the checkout page restores them into the live URL via `history.replaceState`
 * before the SDK reads `window.location`.
 *
 * Keep this list focused — anything unrelated to PayPal must not be persisted
 * here, otherwise we'd risk leaking unrelated query state across navigations.
 */
export const PAYPAL_RETURN_PARAM_KEYS = [
  'paypalReturn',
  'token',
  'PayerID',
  'ba_token',
  'switch_initiated_time',
  'opType',
] as const;

export const PAYPAL_URL_CLEANUP_KEYS = [
  ...PAYPAL_RETURN_PARAM_KEYS,
  'button_session_id',
  'page_session_id',
  'fundingSource',
  'flow',
] as const;

export const PAYPAL_RETURN_PARAMS_SESSION_KEY = '__wayPayPalReturnParams__';

/**
 * Pulls the previously-stashed PayPal return params out of sessionStorage
 * (without consuming them — use {@link consumeStashedPayPalReturnParams} for
 * that). Returns null if nothing is stashed or the value is malformed.
 */
export function readStashedPayPalReturnParams(): Record<string, string> | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PAYPAL_RETURN_PARAMS_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const out: Record<string, string> = {};
    PAYPAL_RETURN_PARAM_KEYS.forEach((key) => {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.length > 0) {
        out[key] = value;
      }
    });
    return Object.keys(out).length > 0 ? out : null;
  } catch {
    return null;
  }
}

/** Same as {@link readStashedPayPalReturnParams} but also clears the entry. */
export function consumeStashedPayPalReturnParams(): Record<string, string> | null {
  const params = readStashedPayPalReturnParams();
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(PAYPAL_RETURN_PARAMS_SESSION_KEY);
    } catch {
      // sessionStorage may be unavailable (e.g. private mode quotas)
    }
  }
  return params;
}

/**
 * Merges any stashed PayPal return params back into `window.location.search`
 * via `history.replaceState` (so the live URL — which the PayPal SDK reads —
 * regains `switch_initiated_time`, `token`, etc. that Angular's router may
 * have stripped during the trampoline navigation). Returns the params that
 * were restored, or null if nothing was stashed / running outside a browser.
 *
 * NOTE: This MUST run before the PayPal SDK Buttons are created on the
 * destination page, otherwise `Buttons.hasReturned()` will read the
 * stripped URL and report false.
 */
export function restorePayPalReturnParamsToUrl(): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  const stashed = consumeStashedPayPalReturnParams();
  if (!stashed) return null;
  try {
    const url = new URL(window.location.href);
    let added = false;
    Object.entries(stashed).forEach(([key, value]) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
        added = true;
      }
    });
    if (!added) return stashed;
    const newRelative =
      url.pathname + (url.search ? url.search : '') + (url.hash || '');
    window.history.replaceState(window.history.state, '', newRelative);
    return stashed;
  } catch {
    return stashed;
  }
}

export interface BraintreePayPalResult {
  nonce: string;
  paymentOption: 'PAYPAL';
  deviceData?: string;
  email?: string;
}

export interface BraintreePayPalConfig {
  authorization: string;
  amount?: string;
  currency?: string;
  flow: 'checkout' | 'vault';
  /** Enable native PayPal app switch when the app is installed on the device. */
  appSwitchWhenAvailable?: boolean;
  /**
   * Return URL after PayPal app switch approval.
   * Defaults to the `/paypal-return` trampoline so the OS doesn't
   * intercept the URL as a Universal Link / App Link.
   */
  returnUrl?: string;
  /**
   * Cancel URL if user cancels in PayPal app.
   * Defaults to the `/paypal-return` trampoline so the OS doesn't
   * intercept the URL as a Universal Link / App Link.
   */
  cancelUrl?: string;
}

/**
 * Builds a return URL pointing to the `/paypal-return` trampoline route.
 *
 * The trampoline path is intentionally NOT claimed by the Way iOS/Android
 * app's Universal Links / App Links configuration. This prevents the OS
 * from intercepting the PayPal return and opening the native Way app
 * instead of the browser where checkout is in progress.
 */
export function buildPayPalTrampolineReturnUrl(returnPath?: string): string {
  if (typeof window === 'undefined') return '';
  const path = returnPath || window.location.pathname || '/checkoutGuest';
  try {
    const url = new URL('/paypal-return', window.location.origin);
    url.searchParams.set('returnPath', path);
    url.searchParams.set('paypalReturn', '1');
    const currentParams = new URLSearchParams(window.location.search);
    const reserved = new Set(['returnPath', 'paypalReturn']);
    currentParams.forEach((value, key) => {
      if (reserved.has(key)) return;
      if (url.searchParams.has(key)) return;
      url.searchParams.append(key, value);
    });
    return url.toString();
  } catch {
    return `${window.location.origin}/paypal-return?returnPath=${encodeURIComponent(path)}&paypalReturn=1`;
  }
}

export interface BraintreePayPalButtonCallbacks {
  onSuccess: (result: BraintreePayPalResult) => void;
  onError: (err: any) => void;
  onCancel?: () => void;
  onClick?: () => boolean | Promise<boolean>;
}

const PAYPAL_SDK_SCRIPT_ID = 'braintree-paypal-sdk-script';

function loadPayPalSDKScript(clientId: string, flow: 'checkout' | 'vault'): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false);
  if ((window as any).paypal?.Buttons) return Promise.resolve(true);

  const existing = document.getElementById(PAYPAL_SDK_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve) => {
      if ((window as any).paypal?.Buttons) {
        resolve(true);
        return;
      }
      existing.addEventListener('load', () => resolve(!!(window as any).paypal?.Buttons));
      existing.addEventListener('error', () => resolve(false));
    });
  }

  const params = new URLSearchParams({
    'client-id': clientId,
    currency: 'USD',
    components: 'buttons',
    'enable-funding': 'paypal',
  });
  if (flow === 'vault') {
    params.set('vault', 'true');
    params.set('intent', 'tokenize');
  } else {
    params.set('intent', 'capture');
    params.set('commit', 'true');
  }

  const script = document.createElement('script');
  script.id = PAYPAL_SDK_SCRIPT_ID;
  script.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
  script.async = true;
  script.setAttribute('data-partner-attribution-id', 'BraintreeWeb');
  document.head.appendChild(script);

  return new Promise((resolve) => {
    script.addEventListener('load', () => resolve(!!(window as any).paypal?.Buttons));
    script.addEventListener('error', () => resolve(false));
  });
}

export class BraintreePayPal {
  private clientInstance: any = null;
  private paypalInstance: any = null;
  private paypalCheckoutInstance: any = null;
  private dataCollectorInstance: any = null;
  private deviceData: string | undefined;
  private destroyed = false;
  private paypalSDKAvailable = false;
  private paypalClientId: string | null = null;
  private renderedButtons: any = null;

  async init(authorization: string): Promise<void> {
    try {
      const isOnReturnPage =
        typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).get('paypalReturn') === '1';
      if (!isOnReturnPage) {
        this.clearPayPalSessionStorage();
        this.clearPayPalLocalStorage();
      }
    } catch {
      // stale storage cleanup is best-effort
    }

    let braintree: any;
    try {
      const mod: any = await import('braintree-web');
      braintree = mod.default ?? mod;
    } catch {
      return;
    }
    if (this.destroyed) return;

    try {
      this.clientInstance = await new Promise<any>((resolve, reject) => {
        braintree.client.create(
          { authorization },
          (err: any, instance: any) => (err ? reject(err) : resolve(instance)),
        );
      });
    } catch {
      return;
    }
    if (this.destroyed) return;

    try {
      if (braintree.dataCollector?.create) {
        const dc = await this.createDataCollector(braintree, this.clientInstance);
        if (dc) {
          this.dataCollectorInstance = dc;
          this.deviceData = braintreeDeviceDataCorrelationOnly(dc.deviceData);
        }
      }
    } catch {
      // device data is optional
    }
    if (this.destroyed) return;

    // Initialize paypalCheckout module (preferred, supports app switch)
    if (braintree.paypalCheckout?.create) {
      try {
        this.paypalCheckoutInstance = await new Promise<any>((resolve, reject) => {
          braintree.paypalCheckout.create(
            { client: this.clientInstance },
            (err: any, instance: any) => (err ? reject(err) : resolve(instance)),
          );
        });
      } catch {
        // paypalCheckout is optional — fall back to legacy paypal module below
      }
    }
    if (this.destroyed) return;

    // Also initialize legacy paypal module for popup-based tokenize fallback
    if (braintree.paypal?.create) {
      try {
        this.paypalInstance = await new Promise<any>((resolve, reject) => {
          braintree.paypal.create(
            { client: this.clientInstance },
            (err: any, instance: any) => (err ? reject(err) : resolve(instance)),
          );
        });
      } catch {
        // legacy fallback is optional
      }
    }
    if (this.destroyed) return;

    if (!this.paypalCheckoutInstance && !this.paypalInstance) {
      return;
    }

    // Resolve PayPal client ID for later SDK loading (deferred until flow is known)
    if (this.paypalCheckoutInstance) {
      try {
        const config = this.clientInstance.getConfiguration?.();
        this.paypalClientId =
          config?.gatewayConfiguration?.paypal?.clientId ??
          config?.analyticsMetadata?.merchantId ??
          'sb';
      } catch {
        this.paypalClientId = 'sb';
      }
    }
  }

  isAvailable(): boolean {
    return !!(this.paypalCheckoutInstance || this.paypalInstance);
  }

  /** Returns true if the PayPal app switch button can potentially be rendered. */
  isAppSwitchAvailable(): boolean {
    return !!this.paypalCheckoutInstance && !!this.paypalClientId;
  }

  /**
   * Renders a PayPal button with app switch support into the given container.
   * When the PayPal app is installed, clicking the button will redirect to the
   * native app instead of the web flow.
   *
   * Returns true if the button was rendered successfully, false if initial setup
   * or the async render failed.
   */
  async renderAppSwitchButton(
    container: string | HTMLElement,
    config: BraintreePayPalConfig,
    callbacks: BraintreePayPalButtonCallbacks,
  ): Promise<boolean> {
    if (!this.paypalCheckoutInstance || !this.paypalClientId) {
      return false;
    }

    // Cross-browser app-switch return fallback.
    const urlReturnHandled = await this.tryConsumeAppSwitchReturnFromUrl(
      config,
      callbacks,
    );
    if (urlReturnHandled) {
      return true;
    }

    if (!this.paypalSDKAvailable) {
      this.paypalSDKAvailable = await this.loadPayPalSDK(config.flow);
    }

    const paypal = (window as any).paypal;
    if (!paypal?.Buttons) {
      return false;
    }

    const isVault = config.flow === 'vault';
    const trampolineUrl = buildPayPalTrampolineReturnUrl();
    const returnUrl = config.returnUrl || trampolineUrl || undefined;
    const cancelUrl = config.cancelUrl || trampolineUrl || undefined;

    const buttonConfig: Record<string, unknown> = {
      fundingSource: paypal.FUNDING.PAYPAL,
      appSwitchWhenAvailable: true,
      style: { borderRadius: 8, height: 45 },
    };

    if (isVault) {
      buttonConfig['createBillingAgreement'] = () => {
        return this.paypalCheckoutInstance.createPayment({
          flow: 'vault',
          returnUrl,
          cancelUrl,
        });
      };
    } else {
      buttonConfig['createOrder'] = () => {
        return this.paypalCheckoutInstance.createPayment({
          flow: 'checkout',
          amount: config.amount,
          currency: config.currency || 'USD',
          intent: 'capture',
          userAction: 'COMMIT',
          returnUrl,
          cancelUrl,
        });
      };
    }

    buttonConfig['onClick'] = (_data: any, actions?: any) => {
      if (!callbacks.onClick) {
        return actions?.resolve ? actions.resolve() : undefined;
      }

      let outcome: boolean | Promise<boolean>;
      try {
        outcome = callbacks.onClick();
      } catch {
        outcome = false;
      }

      if (outcome === false && actions?.reject) {
        try {
          return actions.reject();
        } catch {
          throw new Error('PayPal click rejected by host');
        }
      }

      return Promise.resolve(outcome).then((ok) => {
        if (actions?.resolve && actions?.reject) {
          return ok ? actions.resolve() : actions.reject();
        }
        if (!ok) {
          throw new Error('PayPal click rejected by host');
        }
        return undefined;
      });
    };

    buttonConfig['onApprove'] = (data: any) => {
      return this.paypalCheckoutInstance.tokenizePayment(data).then((payload: any) => {
        callbacks.onSuccess({
          nonce: payload.nonce,
          paymentOption: 'PAYPAL',
          deviceData: this.deviceData,
          email: payload.details?.email,
        });
      });
    };

    buttonConfig['onCancel'] = () => {
      callbacks.onCancel?.();
    };

    buttonConfig['onError'] = (err: any) => {
      callbacks.onError(err);
    };

    try {
      this.renderedButtons = paypal.Buttons(buttonConfig);

      const hasReturned = typeof this.renderedButtons.hasReturned === 'function'
        ? !!this.renderedButtons.hasReturned()
        : false;

      if (hasReturned) {
        this.renderedButtons.resume();
      } else {
        await this.renderedButtons.render(container);
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Cross-browser PayPal app-switch return handler.
   *
   * Reads `token` / `PayerID` (or `ba_token` for vault) from the current
   * URL's query string and calls `paypalCheckoutInstance.tokenizePayment`
   * directly. This bypasses the PayPal SDK's `Buttons.hasReturned()` /
   * `resume()` machinery, which silently fails when the buyer returns in
   * a different browser than the one they started checkout in (because
   * the SDK keeps its pending-order state in sessionStorage, which is
   * per-origin-per-browser).
   *
   * Returns true if it consumed a pending PayPal return — meaning the
   * caller should NOT also render the SDK buttons / call resume(),
   * because callbacks.onSuccess (or onError) has already fired.
   */
  private async tryConsumeAppSwitchReturnFromUrl(
    config: BraintreePayPalConfig,
    callbacks: BraintreePayPalButtonCallbacks,
  ): Promise<boolean> {
    if (typeof window === 'undefined') {
      return false;
    }
    if (!this.paypalCheckoutInstance) {
      return false;
    }

    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (params.get('paypalReturn') !== '1') {
      return false;
    }

    const orderID = params.get('token');
    const payerID = params.get('PayerID');
    const baToken = params.get('ba_token');

    // Previously we deferred to the SDK's Buttons.hasReturned()/resume() when
    // switch_initiated_time was present (indicating same-browser return from
    // the native PayPal app). In practice the SDK's resume path fails after
    // the /paypal-return trampoline navigation because the fresh page load
    // (trampoline → checkout) discards the SDK's sessionStorage context, so
    // hasReturned() returns false and the payment is never completed.
    // We now always tokenize directly from the URL params — this works
    // reliably regardless of whether the return is same-browser or cross-browser.

    let tokenizeData: Record<string, string>;
    const isVault = !!baToken || config.flow === 'vault';
    if (isVault) {
      const billingToken = baToken || orderID;
      if (!billingToken) {
        return false;
      }
      tokenizeData = {
        billingToken,
        ...(payerID ? { payerID } : {}),
      };
    } else {
      if (!orderID || !payerID) {
        return false;
      }
      tokenizeData = { paymentID: orderID, payerID };
    }

    this.cleanPayPalReturnParamsFromUrl(url);
    this.clearPayPalSessionStorage();
    this.clearPayPalLocalStorage();
    this.paypalSDKAvailable = false;

    try {
      const payload = await this.paypalCheckoutInstance.tokenizePayment(tokenizeData);
      callbacks.onSuccess({
        nonce: payload.nonce,
        paymentOption: 'PAYPAL',
        deviceData: this.deviceData,
        email: payload.details?.email,
      });
    } catch (err: any) {
      callbacks.onError(err);
    }
    return true;
  }

  private cleanPayPalReturnParamsFromUrl(url: URL): void {
    let removedAny = false;
    PAYPAL_URL_CLEANUP_KEYS.forEach((k) => {
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k);
        removedAny = true;
      }
    });
    if (!removedAny) {
      return;
    }
    try {
      const newUrl = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState(window.history.state, '', newUrl);
    } catch {
      // history.replaceState may be blocked; non-critical
    }
  }

  private clearPayPalSessionStorage(): void {
    try {
      if (typeof sessionStorage === 'undefined') return;
      const keys = Object.keys(sessionStorage);
      keys.forEach((k) => {
        if (this.isPayPalStorageKey(k)) {
          sessionStorage.removeItem(k);
        }
      });
    } catch {
      // sessionStorage may be unavailable (e.g. private mode quotas)
    }
  }

  /**
   * Clears PayPal SDK state from {@link localStorage}.
   *
   * The PayPal JS SDK persists its adaptive-flow / app-switch decision state
   * under keys like `__paypal_storage__` and `paypal_sdk__experiment_*`. This
   * state survives page reloads and even tab restarts. If we leave it in
   * place after a successful app-switch return, the SDK's next "should I app
   * switch?" decision on the very next checkout can quietly fall back to
   * mobile-web (popup) instead of relaunching the native app — which is the
   * exact symptom we hit on the second checkout in the same browser session.
   */
  private clearPayPalLocalStorage(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const keys = Object.keys(localStorage);
      keys.forEach((k) => {
        if (this.isPayPalStorageKey(k)) {
          localStorage.removeItem(k);
        }
      });
    } catch {
      // localStorage may be unavailable (e.g. quota, private mode)
    }
  }

  /** Returns true if a storage key looks like it belongs to the PayPal SDK. */
  private isPayPalStorageKey(key: string): boolean {
    const lower = key.toLowerCase();
    return (
      lower.includes('paypal') ||
      lower.startsWith('__pp_') ||
      lower.startsWith('pp_') ||
      lower.startsWith('__post_robot_') // post-robot is PayPal's cross-window RPC lib
    );
  }

  /**
   * Handle a pending PayPal app-switch return without needing a rendered button
   * or UI container. Auto-detects vault vs checkout from URL params.
   *
   * Call this after init() when the page URL carries `paypalReturn=1` but the
   * PayPal button container may not be in the DOM yet (e.g. because cart data
   * hasn't loaded).
   */
  async tryHandleAppSwitchReturn(
    callbacks: BraintreePayPalButtonCallbacks,
  ): Promise<boolean> {
    const flow = this.detectFlowFromUrl();
    const config: BraintreePayPalConfig = { authorization: '', flow };
    return this.tryConsumeAppSwitchReturnFromUrl(config, callbacks);
  }

  private detectFlowFromUrl(): 'checkout' | 'vault' {
    if (typeof window === 'undefined') return 'checkout';
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('ba_token')) return 'vault';
    } catch { /* fall through */ }
    return 'checkout';
  }

  /**
   * Check if the page is returning from a PayPal app switch (e.g. new tab scenario).
   * Should be called after init() to handle pending approvals.
   */
  checkAppSwitchReturn(): boolean {
    const hasReturned = !!this.renderedButtons?.hasReturned?.();
    if (!hasReturned) return false;
    this.renderedButtons.resume();
    return true;
  }

  /**
   * Programmatic tokenization using PayPal popup flow (legacy).
   * Prefer renderAppSwitchButton() for native app support.
   */
  async tokenize(config: BraintreePayPalConfig): Promise<BraintreePayPalResult> {
    if (!this.paypalInstance) {
      throw new Error('PayPal not initialized');
    }

    const tokenizeOptions: any = { flow: config.flow };
    if (config.flow === 'checkout' && config.amount) {
      tokenizeOptions.amount = config.amount;
      tokenizeOptions.currency = config.currency || 'USD';
      // 'useraction: commit' makes PayPal's review page show
      // "Pay $X.XX" instead of "Continue / Review Order".
      tokenizeOptions.useraction = 'commit';
    }

    const payload = await new Promise<any>((resolve, reject) => {
      this.paypalInstance.tokenize(tokenizeOptions, (err: any, result: any) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    });

    return {
      nonce: payload.nonce,
      paymentOption: 'PAYPAL',
      deviceData: this.deviceData,
      email: payload.details?.email,
    };
  }

  getDeviceData(): string | undefined {
    return this.deviceData;
  }

  private async loadPayPalSDK(flow: 'checkout' | 'vault'): Promise<boolean> {
    // Always prefer Braintree's SDK loader — it injects the correct client-id
    // and data attributes that the manual fallback might miss, which can affect
    // app-switch eligibility. Only reuse an already-loaded SDK when the
    // Braintree helper is not available (e.g. paypalCheckout module missing).
    if (typeof this.paypalCheckoutInstance?.loadPayPalSDK === 'function') {
      try {
        const sdkOptions: Record<string, unknown> = {
          currency: 'USD',
          components: 'buttons',
          'enable-funding': 'paypal',
          dataAttributes: {
            'partner-attribution-id': 'BraintreeWeb',
          },
        };
        if (flow === 'vault') {
          sdkOptions['vault'] = true;
          sdkOptions['intent'] = 'tokenize';
        } else {
          sdkOptions['intent'] = 'capture';
          sdkOptions['commit'] = 'true';
        }
        await this.paypalCheckoutInstance.loadPayPalSDK(sdkOptions);
        return !!(window as any).paypal?.Buttons;
      } catch {
        // fall through to manual script load
      }
    }

    if (typeof window !== 'undefined' && (window as any).paypal?.Buttons) {
      return true;
    }

    if (!this.paypalClientId) {
      return false;
    }
    return loadPayPalSDKScript(this.paypalClientId, flow);
  }

  private createDataCollector(braintree: any, clientInstance: any): Promise<any> {
    return new Promise((resolve) => {
      const tryCreate = (opts: Record<string, unknown>, fallback: () => void) => {
        try {
          braintree.dataCollector.create(opts, (err: any, dc: any) => {
            if (!err && dc) {
              resolve(dc);
              return;
            }
            fallback();
          });
        } catch {
          fallback();
        }
      };
      tryCreate(
        { client: clientInstance, kount: true, paypal: true },
        () => tryCreate({ client: clientInstance, paypal: true }, () => resolve(null)),
      );
    });
  }

  async teardown(): Promise<void> {
    this.destroyed = true;
    try {
      this.renderedButtons?.close?.();
    } catch {
      // ignore
    }
    try {
      this.paypalCheckoutInstance?.teardown?.(() => {});
    } catch {
      // ignore
    }
    try {
      this.paypalInstance?.teardown?.(() => {});
    } catch {
      // ignore
    }
    try {
      this.dataCollectorInstance?.teardown?.(() => {});
    } catch {
      // ignore
    }
    this.renderedButtons = null;
    this.paypalCheckoutInstance = null;
    this.paypalInstance = null;
    this.clientInstance = null;
    this.dataCollectorInstance = null;
    this.paypalClientId = null;
  }
}
